import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Flex, Form, Spin } from "antd";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../../../store/config";
import { IUploadFileParams } from "../../../../../../../../interfaces/files/files";
import {
  AbsoluteButton55,
  RelativeFlex,
  StyledColumnWrapper,
  StyledForm,
  StyledLayout,
} from "../../../../../../../../components/common/styles";
import {
  IMAGE_BASE_URL,
  VIDEO_BASE_URL,
} from "../../../../../../../../common/config";
import {
  ImageInputField,
  TextInputField,
} from "../../../../../../../../components/inputs";
import {
  createVodStart,
  getVodStart,
  resetVod,
  updateVodStart,
} from "../../../../../../../../store/features/vod/vod-slice";
import {
  ICreateVodPayload,
  IUpdateVodPayload,
  IVod,
} from "../../../../../../../../interfaces/store/vod-interface";
import { TimePickerField } from "../../../../../../../../components/inputs/TimePickerField";
import dayjs from "dayjs";
import { getWorkoutStart } from "../../../../../../../../store/features/workout/workout-slice";
import { blobToBase64 } from "../../../../../../../../utils/utils";

const WorkoutVideoContent = ({
  openNotification,
}: {
  openNotification: (val?: string | null, isWarning?: boolean) => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [imageData, setImageData] = useState<IUploadFileParams | null>(null);
  const [videoData, setVideoData] = useState<IUploadFileParams | null>(null);
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [vod, setVod] = useState<IVod | null>(null);

  const { id: trainerId } = useParams<{ id: string }>();
  const { workoutId } = useParams<{ workoutId: string }>();

  const { workout, loading, error, requestState, operation } = useAppSelector(
    (state) => state.workout
  );

  const {
    vod: stateVod,
    loading: vodLoading,
    error: vodError,
    requestState: vodRequestState,
    operation: vodOperation,
  } = useAppSelector((state) => state.vod);

  useEffect(() => {
    if (vodRequestState === "finish") {
      if (vodOperation === "update" || vodOperation === "create") {
        setIsEditable(false);
        workoutId && dispatch(getWorkoutStart({ workoutId: workoutId }));
      }
    }
  }, [vodRequestState, vodOperation, workoutId, dispatch]);

  useEffect(() => {
    if (error || vodError) {
      openNotification();
    }
  }, [error, vodError, openNotification]);

  useEffect(() => {
    if (!workout) return;
    if (typeof workout.vod === "string") {
      dispatch(getVodStart({ vodId: workout.vod }));
    } else if (workout?.vod?._id) {
      setVod(workout.vod);
    }
  }, [workout, dispatch]);

  useEffect(() => {
    if (stateVod) setVod(stateVod);
  }, [stateVod]);

  const isNew = !vod;

  useEffect(() => {
    if (workout?.vod) {
      const vodId =
        typeof workout.vod === "string" ? workout.vod : workout.vod._id;
      dispatch(getVodStart({ vodId }));
    }
    return () => {
      dispatch(resetVod());
    };
  }, [workout, dispatch]);

  useEffect(() => {
    if (vod) {
      setIsEditable(false);
      form.setFieldsValue({
        name: vod.name,
        duration: dayjs().startOf("day").second(vod.duration),
      });
    } else {
      setIsEditable(true);
    }
  }, [vod, form]);

  const upsertWorkoutVideo = async (values: any) => {
    if (!isEditable) {
      setIsEditable(true);
      return;
    }

    const duration = dayjs(values.duration);
    const durationInSeconds =
      duration.hour() * 3600 + duration.minute() * 60 + duration.second();

    const videoBody = {
      name: values.name,
      duration: durationInSeconds,
      trainer: trainerId || "",
      workout: workoutId || "",
      tags: [] as string[],
    };

    const imageDataPayload = imageData
      ? {
          ...imageData,
          binaryFile: await blobToBase64(imageData.binaryFile as Blob),
          fileType: "image",
        }
      : null;

    const videoDataPayload = videoData
      ? {
          ...videoData,
          binaryFile: await blobToBase64(videoData.binaryFile as Blob),
          fileType: "video",
        }
      : null;

    const upsertVideoBody = {
      vod: { ...videoBody },
      ...(isNew ? {} : { vodId: vod?._id }),
      ...(imageDataPayload && { imageData: imageDataPayload }),
      ...(videoDataPayload && { videoData: videoDataPayload }),
    };

    if (!videoData?.binaryFile) {
      openNotification("Please note that no video was provided", true);
    }

    dispatch(
      isNew
        ? createVodStart(upsertVideoBody as ICreateVodPayload)
        : updateVodStart(upsertVideoBody as IUpdateVodPayload)
    );
  };

  return (
    <>
      <RelativeFlex>
        <AbsoluteButton55 onClick={() => form.submit()}>
          {isEditable ? t("Save") : t("Edit")}
        </AbsoluteButton55>
      </RelativeFlex>

      <Spin spinning={loading || vodLoading}>
        <StyledForm
          form={form}
          onFinish={upsertWorkoutVideo}
          validateTrigger="onSubmit"
        >
          <StyledLayout>
            <StyledColumnWrapper>
              <TextInputField
                name="name"
                label={t("Video Name")}
                isEditable={isEditable}
                rules={[{ required: true, message: t("Required") }]}
              />
              <ImageInputField
                label={t("Video")}
                isEditable={isEditable}
                value={
                  vod?.video ? VIDEO_BASE_URL + "/" + vod?.video : undefined
                }
                onChange={setVideoData}
                type="video"
                isLargeVideoStyle={true}
                aspect={1}
              />
              <TimePickerField
                name="duration"
                label={t("Video Duration")}
                isEditable={isEditable}
                rules={[{ required: true, message: t("Required") }]}
              />
            </StyledColumnWrapper>
            <StyledColumnWrapper>
              <ImageInputField
                label={t("Video Cover Image")}
                isEditable={isEditable}
                value={
                  vod?.image ? IMAGE_BASE_URL + "/" + vod?.image : undefined
                }
                onChange={setImageData}
                aspect={147 / 187}
              />
            </StyledColumnWrapper>
          </StyledLayout>
        </StyledForm>
      </Spin>
    </>
  );
};

export default WorkoutVideoContent;
